import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import { buildApiUrl } from '../../services/config';
import {
  listarEmailConnections,
  listarEnviosAutomaticos,
  listarEnviosOperacao
} from '../../services/montagemPalete';
import {
  EnvioAutomaticoConfig as EnvioConfig,
  DocumentoTipo,
  DOCUMENTO_LABELS,
  TipoEnvio,
  WhatsAppConnection
} from '../../types/config';

interface Props {
  agendamentoId?: number;
  operacaoId?: number;
  envios?: EnvioConfig[];
  onChange?: (envios: EnvioConfig[]) => void;
  readOnly?: boolean;
}

const DOCUMENT_OPTIONS: { value: DocumentoTipo; label: string }[] = Object.entries(DOCUMENTO_LABELS).map(
  ([value, label]) => ({ value: value as DocumentoTipo, label })
);

const EnvioAutomaticoConfig: React.FC<Props> = ({ agendamentoId, operacaoId, envios: externalEnvios, onChange, readOnly }) => {
  const [envios, setEnvios] = useState<EnvioConfig[]>(externalEnvios || []);
  const [whatsappConnections, setWhatsappConnections] = useState<WhatsAppConnection[]>([]);
  const [emailConnections, setEmailConnections] = useState<any[]>([]);
  const [, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<EnvioConfig>({
    tipo: 'whatsapp',
    conexao_id: 0,
    destinatarios: [],
    destinatarios_cc: [],
    destinatarios_cco: [],
    assunto: '',
    mensagem: '',
    formato_mensagem: 'texto',
    documento: 'cracha_palete',
    ativo: true
  });

  const [destinatarioInput, setDestinatarioInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [ccoInput, setCcoInput] = useState('');

  // Load connections
  useEffect(() => {
    loadConnections();
  }, []);

  // Load existing envios if agendamentoId or operacaoId is provided
  useEffect(() => {
    if (agendamentoId || operacaoId) {
      loadEnvios();
    }
  }, [agendamentoId, operacaoId]);

  // Sync external envios
  useEffect(() => {
    if (externalEnvios) {
      setEnvios(externalEnvios);
    }
  }, [externalEnvios]);

  const loadConnections = async () => {
    try {
      const [whatsRes, emailRes] = await Promise.all([
        apiService.request<any>(buildApiUrl('/whatsapp/connections')),
        listarEmailConnections()
      ]);

      setWhatsappConnections(
        ((whatsRes as any)?.data || (whatsRes as any)?.dados || [])
          .filter((c: any) => c.status === 'conectado')
      );
      setEmailConnections(
        ((emailRes as any)?.dados || (emailRes as any)?.data || [])
          .filter((c: any) => c.ativo)
      );
    } catch (err) {
      console.error('Erro ao carregar conexões:', err);
    }
  };

  const loadEnvios = async () => {
    if (!agendamentoId && !operacaoId) return;
    setLoading(true);
    try {
      let res: any;
      if (operacaoId) {
        res = await listarEnviosOperacao(operacaoId);
      } else {
        res = await listarEnviosAutomaticos(agendamentoId!);
      }
      const data = (res as any).data || [];
      setEnvios(data);
      if (onChange) onChange(data);
    } catch (err) {
      console.error('Erro ao carregar envios:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateEnvios = useCallback((newEnvios: EnvioConfig[]) => {
    setEnvios(newEnvios);
    if (onChange) onChange(newEnvios);
  }, [onChange]);

  const addDestinatario = (value: string, field: 'destinatarios' | 'destinatarios_cc' | 'destinatarios_cco') => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const current = formData[field] || [];
    if (!current.includes(trimmed)) {
      setFormData(prev => ({ ...prev, [field]: [...current, trimmed] }));
    }
  };

  const removeDestinatario = (index: number, field: 'destinatarios' | 'destinatarios_cc' | 'destinatarios_cco') => {
    const current = formData[field] || [];
    setFormData(prev => ({ ...prev, [field]: current.filter((_, i) => i !== index) }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, field: 'destinatarios' | 'destinatarios_cc' | 'destinatarios_cco', inputValue: string, setInput: (v: string) => void) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addDestinatario(inputValue, field);
      setInput('');
    }
  };

  const handleSave = () => {
    setError('');

    if (!formData.conexao_id) {
      setError('Selecione uma conexão');
      return;
    }
    if (!formData.destinatarios || formData.destinatarios.length === 0) {
      setError('Adicione pelo menos um destinatário');
      return;
    }

    const newEnvios = [...envios];
    if (editingIndex !== null) {
      newEnvios[editingIndex] = { ...formData };
    } else {
      newEnvios.push({ ...formData });
    }

    updateEnvios(newEnvios);
    resetForm();
  };

  const handleEdit = (index: number) => {
    setFormData({ ...envios[index] });
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleRemove = (index: number) => {
    const newEnvios = envios.filter((_, i) => i !== index);
    updateEnvios(newEnvios);
  };

  const handleToggleAtivo = (index: number) => {
    const newEnvios = [...envios];
    newEnvios[index] = { ...newEnvios[index], ativo: !newEnvios[index].ativo };
    updateEnvios(newEnvios);
  };

  const resetForm = () => {
    setFormData({
      tipo: 'whatsapp',
      conexao_id: 0,
      destinatarios: [],
      destinatarios_cc: [],
      destinatarios_cco: [],
      assunto: '',
      mensagem: '',
      formato_mensagem: 'texto',
      documento: 'cracha_palete',
      ativo: true
    });
    setDestinatarioInput('');
    setCcInput('');
    setCcoInput('');
    setEditingIndex(null);
    setShowForm(false);
    setError('');
  };

  const getConexaoNome = (tipo: TipoEnvio, conexaoId: number) => {
    if (tipo === 'whatsapp') {
      return whatsappConnections.find(c => c.id === conexaoId)?.nome || `Conexão #${conexaoId}`;
    }
    return emailConnections.find((c: any) => c.id === conexaoId)?.nome || `Conexão #${conexaoId}`;
  };

  const renderWhatsAppPreview = () => {
    if (formData.tipo !== 'whatsapp') return null;

    return (
      <div style={{
        background: '#e5ddd5', borderRadius: '8px', padding: '1rem', marginTop: '1rem',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h60v60H0z\' fill=\'none\'/%3E%3Cpath d=\'M30 30m-2 0a2 2 0 104 0 2 2 0 10-4 0\' fill=\'%23d4c9b8\' opacity=\'.3\'/%3E%3C/svg%3E")'
      }}>
        <div style={{ fontSize: '0.75rem', color: '#7f8c8d', marginBottom: '8px', fontWeight: 600 }}>
          Prévia da mensagem WhatsApp
        </div>
        <div style={{
          background: '#dcf8c6', borderRadius: '8px', padding: '8px 12px',
          maxWidth: '320px', marginLeft: 'auto',
          boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
        }}>
          {formData.mensagem && (
            <div style={{ fontSize: '0.85rem', color: '#303030', marginBottom: '6px', whiteSpace: 'pre-wrap' }}>
              {formData.mensagem}
            </div>
          )}
          <div style={{
            background: '#fff', borderRadius: '6px', padding: '8px',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <span style={{ fontSize: '1.5rem' }}>📄</span>
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#333' }}>
                {DOCUMENTO_LABELS[formData.documento]}_OP.pdf
              </div>
              <div style={{ fontSize: '0.7rem', color: '#999' }}>PDF • Documento</div>
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.65rem', color: '#999', marginTop: '4px' }}>
            {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} ✓✓
          </div>
        </div>
        {formData.destinatarios.length > 0 && (
          <div style={{ fontSize: '0.7rem', color: '#7f8c8d', marginTop: '6px' }}>
            Para: {formData.destinatarios.join(', ')}
          </div>
        )}
      </div>
    );
  };

  const renderEmailPreview = () => {
    if (formData.tipo !== 'email') return null;

    return (
      <div style={{
        background: '#f5f5f5', borderRadius: '8px', padding: '1rem', marginTop: '1rem',
        border: '1px solid #ddd'
      }}>
        <div style={{ fontSize: '0.75rem', color: '#7f8c8d', marginBottom: '8px', fontWeight: 600 }}>
          Prévia do email
        </div>
        <div style={{
          background: '#fff', borderRadius: '6px', padding: '12px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '8px' }}>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              <strong>Para:</strong> {formData.destinatarios.join(', ') || '(destinatários)'}
            </div>
            {(formData.destinatarios_cc || []).length > 0 && (
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                <strong>Cc:</strong> {formData.destinatarios_cc!.join(', ')}
              </div>
            )}
            {(formData.destinatarios_cco || []).length > 0 && (
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                <strong>Cco:</strong> {formData.destinatarios_cco!.join(', ')}
              </div>
            )}
            <div style={{ fontSize: '0.85rem', color: '#333', fontWeight: 600, marginTop: '4px' }}>
              {formData.assunto || '(sem assunto)'}
            </div>
          </div>
          {formData.mensagem && (
            <div style={{ fontSize: '0.85rem', color: '#333', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
              {formData.formato_mensagem === 'html'
                ? <div dangerouslySetInnerHTML={{ __html: formData.mensagem }} />
                : formData.mensagem
              }
            </div>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: '#f8f9fa', borderRadius: '4px', padding: '6px 10px'
          }}>
            <span style={{ fontSize: '1.2rem' }}>📎</span>
            <span style={{ fontSize: '0.8rem', color: '#666' }}>
              {DOCUMENTO_LABELS[formData.documento]}_OP.pdf
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (readOnly && envios.length === 0) return null;

  return (
    <div style={{
      border: '1px solid #d0d7de', borderRadius: '8px', padding: '1rem',
      background: '#fafbfc', marginTop: '1rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#2c3e50' }}>
            Envio Automático de Documentos
          </h4>
          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#7f8c8d' }}>
            Configure o envio automático de etiquetas e documentos via WhatsApp ou Email
          </p>
        </div>
        {!readOnly && !showForm && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{
              padding: '6px 14px', background: '#04364F', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600
            }}
          >
            + Adicionar Envio
          </button>
        )}
      </div>

      {/* Existing envios list */}
      {envios.length > 0 && (
        <div style={{ display: 'grid', gap: '8px', marginBottom: showForm ? '1rem' : 0 }}>
          {envios.map((envio, idx) => (
            <div key={idx} style={{
              border: '1px solid #e0e0e0', borderRadius: '6px', padding: '10px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: envio.ativo ? '#fff' : '#f5f5f5',
              opacity: envio.ativo ? 1 : 0.6
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                  <span style={{ fontSize: '1rem' }}>{envio.tipo === 'whatsapp' ? '💬' : '📧'}</span>
                  <strong style={{ fontSize: '0.85rem', color: '#2c3e50' }}>
                    {envio.tipo === 'whatsapp' ? 'WhatsApp' : 'Email'}
                  </strong>
                  <span style={{
                    padding: '1px 8px', borderRadius: '10px', fontSize: '0.7rem',
                    background: '#e8f4fd', color: '#0c5460', fontWeight: 600
                  }}>
                    {DOCUMENTO_LABELS[envio.documento]}
                  </span>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#7f8c8d' }}>
                  {getConexaoNome(envio.tipo, envio.conexao_id)} → {envio.destinatarios.join(', ')}
                </div>
              </div>
              {!readOnly && (
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => handleToggleAtivo(idx)} title={envio.ativo ? 'Desativar' : 'Ativar'}
                    style={{ padding: '4px 8px', border: '1px solid #ccc', background: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                    {envio.ativo ? '⏸' : '▶'}
                  </button>
                  <button onClick={() => handleEdit(idx)}
                    style={{ padding: '4px 8px', border: '1px solid #04364F', background: '#fff', color: '#04364F', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                    Editar
                  </button>
                  <button onClick={() => handleRemove(idx)}
                    style={{ padding: '4px 8px', border: '1px solid #e74c3c', background: '#fff', color: '#e74c3c', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                    ✕
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Form */}
      {showForm && !readOnly && (
        <div style={{ background: '#fff', border: '1px solid #d0d7de', borderRadius: '8px', padding: '1rem' }}>
          {error && (
            <div style={{ padding: '8px 12px', background: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.82rem' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Left column: Config */}
            <div>
              {/* Tipo */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                  Tipo de Envio
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(['whatsapp', 'email'] as TipoEnvio[]).map(t => (
                    <button key={t} type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tipo: t, conexao_id: 0 }))}
                      style={{
                        padding: '6px 16px', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.82rem', fontWeight: 600,
                        border: formData.tipo === t ? '2px solid #04364F' : '1px solid #ccc',
                        background: formData.tipo === t ? '#e8f4fd' : '#fff',
                        color: formData.tipo === t ? '#04364F' : '#666'
                      }}>
                      {t === 'whatsapp' ? '💬 WhatsApp' : '📧 Email'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conexão */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                  {formData.tipo === 'whatsapp' ? 'Conexão WhatsApp' : 'Conta de Email'} *
                </label>
                <select
                  value={formData.conexao_id}
                  onChange={e => setFormData(prev => ({ ...prev, conexao_id: parseInt(e.target.value) }))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                >
                  <option value={0}>Selecione...</option>
                  {(formData.tipo === 'whatsapp' ? whatsappConnections : emailConnections).map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.nome} {formData.tipo === 'whatsapp' ? `(${c.numero_telefone})` : `(${c.smtp_user})`}
                    </option>
                  ))}
                </select>
                {formData.tipo === 'whatsapp' && whatsappConnections.length === 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#e74c3c', margin: '4px 0 0' }}>
                    Nenhuma conexão WhatsApp conectada. Configure em Configurações → WhatsApp.
                  </p>
                )}
                {formData.tipo === 'email' && emailConnections.length === 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#e74c3c', margin: '4px 0 0' }}>
                    Nenhuma conexão de email ativa. Configure na aba Email (SMTP).
                  </p>
                )}
              </div>

              {/* Destinatários */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                  {formData.tipo === 'whatsapp' ? 'Números de WhatsApp' : 'Destinatários (Para)'} *
                </label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text" value={destinatarioInput}
                    onChange={e => setDestinatarioInput(e.target.value)}
                    onKeyDown={e => handleKeyDown(e, 'destinatarios', destinatarioInput, setDestinatarioInput)}
                    placeholder={formData.tipo === 'whatsapp' ? '55119999999 (pressione Enter)' : 'email@exemplo.com (pressione Enter)'}
                    style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <button type="button"
                    onClick={() => { addDestinatario(destinatarioInput, 'destinatarios'); setDestinatarioInput(''); }}
                    style={{ padding: '6px 12px', background: '#04364F', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                    +
                  </button>
                </div>
                {formData.destinatarios.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                    {formData.destinatarios.map((d, i) => (
                      <span key={i} style={{
                        padding: '2px 8px', background: '#e8f4fd', borderRadius: '12px',
                        fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '4px'
                      }}>
                        {d}
                        <button type="button" onClick={() => removeDestinatario(i, 'destinatarios')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: 700, fontSize: '0.8rem', padding: 0 }}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* CC and CCO for email */}
              {formData.tipo === 'email' && (
                <>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                      Cc (opcional)
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input type="text" value={ccInput}
                        onChange={e => setCcInput(e.target.value)}
                        onKeyDown={e => handleKeyDown(e, 'destinatarios_cc', ccInput, setCcInput)}
                        placeholder="email@exemplo.com (pressione Enter)"
                        style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                      />
                      <button type="button"
                        onClick={() => { addDestinatario(ccInput, 'destinatarios_cc'); setCcInput(''); }}
                        style={{ padding: '6px 12px', background: '#04364F', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                        +
                      </button>
                    </div>
                    {(formData.destinatarios_cc || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                        {formData.destinatarios_cc!.map((d, i) => (
                          <span key={i} style={{
                            padding: '2px 8px', background: '#fff3cd', borderRadius: '12px', fontSize: '0.78rem',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}>
                            {d}
                            <button type="button" onClick={() => removeDestinatario(i, 'destinatarios_cc')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: 700, fontSize: '0.8rem', padding: 0 }}>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                      Cco (opcional)
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input type="text" value={ccoInput}
                        onChange={e => setCcoInput(e.target.value)}
                        onKeyDown={e => handleKeyDown(e, 'destinatarios_cco', ccoInput, setCcoInput)}
                        placeholder="email@exemplo.com (pressione Enter)"
                        style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                      />
                      <button type="button"
                        onClick={() => { addDestinatario(ccoInput, 'destinatarios_cco'); setCcoInput(''); }}
                        style={{ padding: '6px 12px', background: '#04364F', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                        +
                      </button>
                    </div>
                    {(formData.destinatarios_cco || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                        {formData.destinatarios_cco!.map((d, i) => (
                          <span key={i} style={{
                            padding: '2px 8px', background: '#f8d7da', borderRadius: '12px', fontSize: '0.78rem',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}>
                            {d}
                            <button type="button" onClick={() => removeDestinatario(i, 'destinatarios_cco')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontWeight: 700, fontSize: '0.8rem', padding: 0 }}>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Assunto */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                      Assunto
                    </label>
                    <input type="text" value={formData.assunto || ''}
                      onChange={e => setFormData(prev => ({ ...prev, assunto: e.target.value }))}
                      placeholder="Etiquetas Montagem de Carga - OP #{numero}"
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                    />
                  </div>
                </>
              )}

              {/* Documento */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                  Documento a Anexar *
                </label>
                <select
                  value={formData.documento}
                  onChange={e => setFormData(prev => ({ ...prev, documento: e.target.value as DocumentoTipo }))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                >
                  {DOCUMENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Mensagem */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.82rem', color: '#555' }}>
                  {formData.tipo === 'whatsapp' ? 'Mensagem' : 'Corpo do Email'}
                  {formData.tipo === 'email' && (
                    <select value={formData.formato_mensagem}
                      onChange={e => setFormData(prev => ({ ...prev, formato_mensagem: e.target.value as 'texto' | 'html' }))}
                      style={{ marginLeft: '8px', padding: '2px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.75rem' }}>
                      <option value="texto">Texto Simples</option>
                      <option value="html">HTML</option>
                    </select>
                  )}
                </label>
                <textarea
                  value={formData.mensagem || ''}
                  onChange={e => setFormData(prev => ({ ...prev, mensagem: e.target.value }))}
                  placeholder={formData.tipo === 'whatsapp'
                    ? 'Segue em anexo as etiquetas da montagem de carga.'
                    : 'Segue em anexo o documento da montagem de carga.'}
                  rows={3}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', resize: 'vertical' }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={resetForm}
                  style={{ padding: '6px 14px', border: '1px solid #ccc', background: '#fff', color: '#666', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
                  Cancelar
                </button>
                <button type="button" onClick={handleSave}
                  style={{ padding: '6px 14px', background: '#04364F', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                  {editingIndex !== null ? 'Atualizar' : 'Adicionar'}
                </button>
              </div>
            </div>

            {/* Right column: Preview */}
            <div>
              {renderWhatsAppPreview()}
              {renderEmailPreview()}

              {!formData.mensagem && !formData.destinatarios.length && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: '200px', color: '#ccc', fontSize: '0.85rem',
                  border: '2px dashed #e0e0e0', borderRadius: '8px', marginTop: '1rem'
                }}>
                  Preencha os campos para ver a prévia
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvioAutomaticoConfig;
